import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';

const MODULE_PARENT_URL_KEY = 'moduleParentUrl';

@Injectable({
    providedIn: 'root',
})
export class ModuleGlobalService {
    private modulePath = '';

    constructor(private _router: Router) {}

    getParentUrl(): string {
        // sessionStorage is the source of truth so every injector instance stays in sync
        // (lazy modules / duplicate providers can otherwise keep a stale in-memory path like `/modules`).
        const stored = sessionStorage.getItem(MODULE_PARENT_URL_KEY);
        if (stored !== null) {
            this.modulePath = stored;
            return stored;
        }

        this.modulePath = '';
        return '';
    }

    setParentUrl(path: string): void {
        this.modulePath = path || '';

        if (this.modulePath) {
            sessionStorage.setItem(MODULE_PARENT_URL_KEY, this.modulePath);
        } else {
            sessionStorage.removeItem(MODULE_PARENT_URL_KEY);
        }
    }

    /** Store the current browser URL when the user is on a catalog page. */
    captureCatalogReturnUrl(): void {
        const url = this.getCurrentBrowserUrl();
        if (url.startsWith('/catalog')) {
            this.setParentUrl(url);
        }
    }

    /** Avoid storing module routes as the return target (e.g. participant-mode preview). */
    captureReturnUrlIfExternal(path: string): void {
        const normalized = path.split('?')[0];
        if (path && !this.isModulePath(normalized)) {
            this.setParentUrl(path);
        }
    }

    isModulePath(path: string): boolean {
        return path === '/modules' || path.startsWith('/modules/');
    }

    resolveBackUrl(currentPath: string, fallback = '/modules'): string {
        const parentUrl = this.getParentUrl();
        if (!parentUrl) {
            return fallback;
        }

        const parentPath = parentUrl.split('?')[0];
        const normalizedCurrentPath = currentPath.split('?')[0];

        // Same URL or the modules list itself → use fallback
        if (parentPath === normalizedCurrentPath || parentPath === '/modules') {
            return fallback;
        }

        // Same module (e.g. preview / tab noise) → fallback; different module (e.g. program → course) is valid
        const parentModuleId = this.extractModuleId(parentPath);
        const currentModuleId = this.extractModuleId(normalizedCurrentPath);
        if (parentModuleId && currentModuleId && parentModuleId === currentModuleId) {
            return fallback;
        }

        return parentUrl.startsWith('/') ? parentUrl : `/${parentUrl}`;
    }

    private extractModuleId(path: string): string | null {
        const match = /^\/modules\/([^/?]+)/.exec(path);
        return match ? match[1] : null;
    }

    /** Navigate back and clear parent URL after arrival. */
    navigateBack(currentPath: string, fallback = '/modules'): void {
        const backUrl = this.resolveBackUrl(currentPath, fallback);
        const normalizedBackUrl = backUrl.startsWith('/') ? backUrl : `/${backUrl}`;

        // Keep moduleParentUrl until NavigationEnd so UserCatalogGuard can allow catalog return navigation.
        // Use navigateByUrl for full paths (NGXS Navigate([fullPath]) can mishandle slash-containing segments).
        void this._router.navigateByUrl(normalizedBackUrl);

        this._router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                take(1)
            )
            .subscribe(() => this.setParentUrl(''));
    }

    private getCurrentBrowserUrl(): string {
        return window.location.pathname + window.location.search;
    }
}
